import { a as __rest } from '../../tslib.es6.esm.js';
import { jsxs, jsx } from 'react/jsx-runtime';
import React__default, { forwardRef } from 'react';
import { startOfDay, isAfter, endOfDay, isBefore, startOfMonth, endOfMonth, isSameYear, isSameMonth, getDaysInMonth, addMonths, subMonths } from 'date-fns';
import styles from './Calendar.styles.esm.js';
import { CalendarContent } from './components/CalendarContent.esm.js';
import { CalendarHeader } from './components/CalendarHeader.esm.js';
import { cn } from '../../utils/cn.esm.js';

/**
 * Calendar component for date selection with various constraints
 * Built with accessibility and keyboard navigation in mind
 */
const Calendar = forwardRef((_a, ref) => {
    var { className, inverse, value, defaultActiveDate, disablePastTo, disableFutureFrom, disabledDaysOfTheWeek, disablePastDates, disableFutureDates, onValueChange } = _a, props = __rest(_a, ["className", "inverse", "value", "defaultActiveDate", "disablePastTo", "disableFutureFrom", "disabledDaysOfTheWeek", "disablePastDates", "disableFutureDates", "onValueChange"]);
    const headerRef = React__default.useRef(null);
    const contentRef = React__default.useRef(null);
    const { container } = styles({
        class: className,
        inverse,
    });
    // Today
    const currentDate = React__default.useMemo(() => new Date(), []);
    // The date is showing in calendar
    const [calendarDate, setCalendarDate] = React__default.useState(defaultActiveDate || new Date());
    // Selected date
    const [selectedDate, setSelectedDate] = React__default.useState(defaultActiveDate || null);
    React__default.useEffect(() => {
        if (value) {
            setSelectedDate(value);
            setCalendarDate(value);
        }
    }, [value]);
    const calendarDateYear = React__default.useMemo(() => calendarDate === null || calendarDate === void 0 ? void 0 : calendarDate.getFullYear(), [calendarDate]);
    const calendarDateMonth = React__default.useMemo(() => calendarDate.getMonth(), [calendarDate]);
    const handleDayClick = (day) => {
        const pickedDate = new Date(calendarDateYear, calendarDateMonth, day);
        onValueChange === null || onValueChange === void 0 ? void 0 : onValueChange(pickedDate);
        setSelectedDate(pickedDate);
    };
    const handlePreviousMonth = () => {
        setCalendarDate((prevDate) => subMonths(prevDate, 1));
    };
    const handleNextMonth = () => {
        setCalendarDate((prevDate) => addMonths(prevDate, 1));
    };
    React__default.useEffect(() => {
        if (disableFutureFrom) {
            const disableFutureFromDate = startOfDay(new Date(disableFutureFrom));
            if (isAfter(calendarDate, disableFutureFromDate)) {
                setCalendarDate(disableFutureFromDate);
                if (selectedDate && isAfter(selectedDate, disableFutureFromDate)) {
                    setSelectedDate(null);
                }
            }
        }
        if (disablePastTo) {
            const disablePastToDate = endOfDay(new Date(disablePastTo));
            if (isBefore(calendarDate, disablePastToDate)) {
                setCalendarDate(disablePastToDate);
                if (selectedDate && isBefore(selectedDate, disablePastToDate)) {
                    setSelectedDate(null);
                }
            }
        }
        if (disablePastDates &&
            isBefore(calendarDate, startOfMonth(currentDate))) {
            setCalendarDate(currentDate);
        }
        if (disableFutureDates &&
            isAfter(calendarDate, endOfMonth(currentDate))) {
            setCalendarDate(currentDate);
        }
        // Ensure selectedDate is within the current day range
        if (selectedDate) {
            const startOfCurrentDay = startOfDay(currentDate);
            const endOfCurrentDay = endOfDay(currentDate);
            // Unselect when disabled weekends
            const day = selectedDate.getDay();
            if (disabledDaysOfTheWeek === null || disabledDaysOfTheWeek === void 0 ? void 0 : disabledDaysOfTheWeek.includes(day)) {
                setSelectedDate(null);
                return;
            }
            // Select current when disable past dates
            if (disablePastDates && selectedDate < endOfCurrentDay) {
                setSelectedDate(currentDate);
                return;
            }
            // Select current when disable future dates
            if (disableFutureDates && selectedDate > startOfCurrentDay) {
                setSelectedDate(currentDate);
                return;
            }
        }
    }, [
        selectedDate,
        disablePastTo,
        disableFutureFrom,
        disabledDaysOfTheWeek,
        disablePastDates,
        disableFutureDates,
        currentDate,
        calendarDate,
    ]);
    const getFocusableElements = (ref) => {
        if (!ref.current)
            return [];
        return Array.from(ref.current.querySelectorAll('button:not([disabled]), [tabindex]:not([tabindex="-1"])'));
    };
    const handleKeyDown = (event) => {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        const header = getFocusableElements(headerRef);
        const content = getFocusableElements(contentRef);
        const focusableElements = [...header, ...content];
        const currentIndex = focusableElements.indexOf(document.activeElement);
        const currentContentIndex = content.indexOf(document.activeElement);
        switch (event.key) {
            case "Enter":
                if (currentIndex !== -1 && focusableElements[currentIndex]) {
                    focusableElements[currentIndex].click();
                }
                break;
            case "ArrowRight": {
                const nextIndex = currentContentIndex + 1;
                if (nextIndex <= content.length - 1) {
                    (_a = content[nextIndex]) === null || _a === void 0 ? void 0 : _a.focus();
                    event.preventDefault();
                    break;
                }
                setCalendarDate((prevDate) => {
                    const newDate = new Date(prevDate.getFullYear(), prevDate.getMonth() + 1, 1);
                    setTimeout(() => {
                        var _a;
                        const updateContent = getFocusableElements(contentRef);
                        const updateHeader = getFocusableElements(headerRef);
                        const updatedFocusableElements = [
                            ...updateHeader,
                            ...updateContent,
                        ];
                        (_a = updatedFocusableElements === null || updatedFocusableElements === void 0 ? void 0 : updatedFocusableElements[2]) === null || _a === void 0 ? void 0 : _a.focus();
                    }, 0);
                    return newDate;
                });
                event.preventDefault();
                break;
            }
            case "ArrowLeft": {
                const prevIndex = currentContentIndex - 1;
                if (prevIndex >= 0) {
                    (_b = content[prevIndex]) === null || _b === void 0 ? void 0 : _b.focus();
                    event.preventDefault();
                    break;
                }
                setCalendarDate((prevDate) => {
                    const newDate = new Date(prevDate.getFullYear(), prevDate.getMonth() - 1, 1);
                    setTimeout(() => {
                        var _a;
                        const updateContent = getFocusableElements(contentRef);
                        const updateHeader = getFocusableElements(headerRef);
                        const updatedFocusableElements = [
                            ...updateHeader,
                            ...updateContent,
                        ];
                        (_a = updatedFocusableElements === null || updatedFocusableElements === void 0 ? void 0 : updatedFocusableElements[(updatedFocusableElements === null || updatedFocusableElements === void 0 ? void 0 : updatedFocusableElements.length) - 1]) === null || _a === void 0 ? void 0 : _a.focus();
                    }, 0);
                    return newDate;
                });
                event.preventDefault();
                break;
            }
            case "ArrowUp": {
                const currentdateValue = focusableElements[currentIndex].getAttribute("data-value");
                if (!currentdateValue)
                    return;
                if (Number(currentdateValue) <= 7) {
                    const currentDayNumber = Number(currentdateValue);
                    const currentDow = new Date(calendarDateYear, calendarDateMonth, currentDayNumber).getDay();
                    setCalendarDate((prevDate) => {
                        const newDate = new Date(prevDate.getFullYear(), prevDate.getMonth() - 1, 1);
                        setTimeout(() => {
                            var _a;
                            // Find the last occurrence of the same weekday in the previous month
                            const daysInPrev = getDaysInMonth(newDate);
                            const lastOfPrev = new Date(newDate.getFullYear(), newDate.getMonth(), daysInPrev);
                            const diff = (lastOfPrev.getDay() - currentDow + 7) % 7;
                            let targetDay = daysInPrev - diff;
                            const updatedContent = getFocusableElements(contentRef);
                            const findEl = (d) => updatedContent.find((el) => el.getAttribute("data-value") === String(d));
                            let targetEl = findEl(targetDay);
                            while (!targetEl && targetDay > 0) {
                                targetDay -= 7; // step back one week
                                targetEl = findEl(targetDay);
                            }
                            if (targetEl) {
                                targetEl.focus();
                            }
                            else {
                                // fallback: last focusable in the grid
                                (_a = updatedContent === null || updatedContent === void 0 ? void 0 : updatedContent[(updatedContent === null || updatedContent === void 0 ? void 0 : updatedContent.length) - 1]) === null || _a === void 0 ? void 0 : _a.focus();
                            }
                        }, 0);
                        return newDate;
                    });
                    event.preventDefault();
                    return;
                }
                const targetValue = Number(currentdateValue) - 7;
                const targetIndex = focusableElements.findIndex((element) => Number(element.getAttribute("data-value")) === targetValue);
                // Focus the target element if it exists
                if (targetIndex !== -1) {
                    (_c = focusableElements[targetIndex]) === null || _c === void 0 ? void 0 : _c.focus();
                }
                event.preventDefault();
                break;
            }
            case "ArrowDown": {
                const currentdateValue = focusableElements[currentIndex].getAttribute("data-value");
                if (!currentdateValue)
                    return;
                if (Number(currentdateValue) > getDaysInMonth(calendarDate) - 7) {
                    const currentDayNumber = Number(currentdateValue);
                    const currentDow = new Date(calendarDateYear, calendarDateMonth, currentDayNumber).getDay();
                    setCalendarDate((prevDate) => {
                        const newDate = new Date(prevDate.getFullYear(), prevDate.getMonth() + 1, 1);
                        setTimeout(() => {
                            var _a;
                            // Find the first occurrence of the same weekday in the next month
                            const firstOfNext = new Date(newDate.getFullYear(), newDate.getMonth(), 1);
                            const diff = (currentDow - firstOfNext.getDay() + 7) % 7; // offset to same weekday
                            let targetDay = 1 + diff;
                            const updatedContent = getFocusableElements(contentRef);
                            const findEl = (d) => updatedContent.find((el) => el.getAttribute("data-value") === String(d));
                            let targetEl = findEl(targetDay);
                            const daysInNext = getDaysInMonth(newDate);
                            while (!targetEl && targetDay <= daysInNext) {
                                targetDay += 7; // step forward one week
                                targetEl = findEl(targetDay);
                            }
                            if (targetEl) {
                                targetEl.focus();
                            }
                            else {
                                // fallback: first focusable in the grid
                                (_a = updatedContent === null || updatedContent === void 0 ? void 0 : updatedContent[0]) === null || _a === void 0 ? void 0 : _a.focus();
                            }
                        }, 0);
                        return newDate;
                    });
                    event.preventDefault();
                    return;
                }
                const targetValue = Number(currentdateValue) + 7;
                const targetIndex = focusableElements.findIndex((element) => Number(element.getAttribute("data-value")) === targetValue);
                if (targetIndex !== -1) {
                    (_d = focusableElements[targetIndex]) === null || _d === void 0 ? void 0 : _d.focus();
                }
                event.preventDefault();
                break;
            }
            case "PageUp": {
                // If past dates are disabled, do not allow navigation to the previous month or year
                if (disablePastDates &&
                    isSameYear(calendarDate, currentDate) &&
                    (event.shiftKey || isSameMonth(calendarDate, currentDate))) {
                    return;
                }
                // If disablePastTo is set, do not allow navigation to the previous month
                if (disablePastTo) {
                    const disablePastToDate = endOfMonth(new Date(disablePastTo));
                    const calendarDateStart = startOfMonth(new Date(calendarDateYear, calendarDateMonth));
                    if (isBefore(calendarDateStart, disablePastToDate)) {
                        return;
                    }
                }
                // Navigate to the previous month
                setCalendarDate((prevDate) => {
                    const newDate = new Date(prevDate.getFullYear() - (event.shiftKey ? 1 : 0), prevDate.getMonth() - (event.shiftKey ? 0 : 1), 1);
                    const currentIndex = focusableElements.indexOf(document.activeElement);
                    // Move focus to the new day
                    setTimeout(() => {
                        var _a, _b, _c;
                        const updateContent = getFocusableElements(contentRef);
                        const updateHeader = getFocusableElements(headerRef);
                        const updatedFocusableElements = [
                            ...updateHeader,
                            ...updateContent,
                        ];
                        if (currentIndex !== -1) {
                            const currentFocusValue = (_a = focusableElements[currentIndex]) === null || _a === void 0 ? void 0 : _a.getAttribute("data-value");
                            const lastFocusableElementValue = updatedFocusableElements[updatedFocusableElements.length - 1].getAttribute("data-value");
                            if (!currentFocusValue || !lastFocusableElementValue) {
                                return;
                            }
                            if (currentFocusValue > lastFocusableElementValue) {
                                (_b = updatedFocusableElements[(updatedFocusableElements === null || updatedFocusableElements === void 0 ? void 0 : updatedFocusableElements.length) - 1]) === null || _b === void 0 ? void 0 : _b.focus();
                                return;
                            }
                            const newFocusElement = updatedFocusableElements.find((element) => element.getAttribute("data-value") === currentFocusValue);
                            if (newFocusElement) {
                                newFocusElement === null || newFocusElement === void 0 ? void 0 : newFocusElement.focus();
                            }
                            else {
                                (_c = updatedFocusableElements[updatedFocusableElements.length - 1]) === null || _c === void 0 ? void 0 : _c.focus();
                            }
                        }
                    }, 0);
                    return newDate;
                });
                event.preventDefault();
                break;
            }
            case "PageDown": {
                // If disableFutureFrom is set, do not allow navigation to the next month or year
                if (disableFutureFrom) {
                    const disableFutureFromDate = startOfMonth(new Date(disableFutureFrom));
                    const calendarDateStart = startOfMonth(new Date(calendarDateYear, calendarDateMonth));
                    if (isBefore(calendarDateStart, disableFutureFromDate)) {
                        return;
                    }
                }
                if (disableFutureDates &&
                    isSameYear(calendarDate, currentDate) &&
                    (event.shiftKey || isSameMonth(calendarDate, currentDate))) {
                    return;
                }
                // Navigate to the next month
                setCalendarDate((prevDate) => {
                    const newDate = new Date(prevDate.getFullYear() + (event.shiftKey ? 1 : 0), prevDate.getMonth() + (event.shiftKey ? 0 : 1), 1);
                    const currentIndex = focusableElements.indexOf(document.activeElement);
                    // Move focus to the new day
                    setTimeout(() => {
                        var _a, _b, _c;
                        const updateContent = getFocusableElements(contentRef);
                        const updateHeader = getFocusableElements(headerRef);
                        const updatedFocusableElements = [
                            ...updateHeader,
                            ...updateContent,
                        ];
                        if (currentIndex !== -1) {
                            const currentFocusValue = (_a = focusableElements[currentIndex]) === null || _a === void 0 ? void 0 : _a.getAttribute("data-value");
                            const lastFocusableElementValue = updatedFocusableElements[updatedFocusableElements.length - 1].getAttribute("data-value");
                            if (!currentFocusValue || !lastFocusableElementValue) {
                                return;
                            }
                            if (currentFocusValue > lastFocusableElementValue) {
                                (_b = updatedFocusableElements[(updatedFocusableElements === null || updatedFocusableElements === void 0 ? void 0 : updatedFocusableElements.length) - 1]) === null || _b === void 0 ? void 0 : _b.focus();
                                return;
                            }
                            const newFocusElement = updatedFocusableElements.find((element) => element.getAttribute("data-value") === currentFocusValue);
                            if (newFocusElement) {
                                newFocusElement === null || newFocusElement === void 0 ? void 0 : newFocusElement.focus();
                            }
                            else {
                                (_c = updatedFocusableElements[(updatedFocusableElements === null || updatedFocusableElements === void 0 ? void 0 : updatedFocusableElements.length) - 1]) === null || _c === void 0 ? void 0 : _c.focus();
                            }
                        }
                    }, 0);
                    return newDate;
                });
                event.preventDefault();
                break;
            }
            case "Home": {
                const currentWeek = (_e = document.activeElement) === null || _e === void 0 ? void 0 : _e.getAttribute("data-week");
                if (currentWeek) {
                    const firstDayOfWeek = (_f = contentRef.current) === null || _f === void 0 ? void 0 : _f.querySelector(`[data-week="${currentWeek}"]:not([disabled])`);
                    firstDayOfWeek === null || firstDayOfWeek === void 0 ? void 0 : firstDayOfWeek.focus();
                }
                event.preventDefault();
                break;
            }
            case "End": {
                const currentWeek = (_g = document.activeElement) === null || _g === void 0 ? void 0 : _g.getAttribute("data-week");
                if (currentWeek) {
                    const daysOfWeek = (_h = contentRef.current) === null || _h === void 0 ? void 0 : _h.querySelectorAll(`[data-week="${currentWeek}"]:not([disabled])`);
                    const lastDayOfWeek = daysOfWeek === null || daysOfWeek === void 0 ? void 0 : daysOfWeek[daysOfWeek.length - 1];
                    lastDayOfWeek === null || lastDayOfWeek === void 0 ? void 0 : lastDayOfWeek.focus();
                }
                event.preventDefault();
                break;
            }
        }
    };
    return (jsxs("div", Object.assign({ ref: ref, "data-testid": "calendar", className: cn(container(), className), onKeyDown: handleKeyDown }, props, { children: [jsx(CalendarHeader, { ref: headerRef, calendarDate: calendarDate, currentDate: currentDate, disablePastDates: disablePastDates, disableFutureDates: disableFutureDates, disablePastTo: disablePastTo, disableFutureFrom: disableFutureFrom, onPreviousMonth: handlePreviousMonth, onNextMonth: handleNextMonth, inverse: inverse }), jsx(CalendarContent, { ref: contentRef, inverse: inverse, currentDate: currentDate, calendarDateYear: calendarDateYear, calendarDateMonth: calendarDateMonth, selectedDate: selectedDate, disabledDaysOfTheWeek: disabledDaysOfTheWeek, disablePastDates: disablePastDates, disableFutureDates: disableFutureDates, disablePastTo: disablePastTo, disableFutureFrom: disableFutureFrom, onDayClick: handleDayClick })] })));
});
Calendar.displayName = "Calendar";

export { Calendar };
